import React, { useEffect, useMemo, useState } from "react";
import "./CompareCases.css";

const API_URL = process.env.REACT_APP_API_URL;

/* =========================
   UTILITAIRES : CAS ACTIF
========================= */

function getActiveCaseId() {
  const qs = new URLSearchParams(window.location.search);
  const fromQuery = qs.get("caseId");
  if (fromQuery) return fromQuery;

  const fromActive = localStorage.getItem("active_case_id");
  if (fromActive) return fromActive;

  const fromLegacy = localStorage.getItem("caseId");
  if (fromLegacy) return fromLegacy;

  return "";
}

function getActiveEnterpriseId() {
  const qs = new URLSearchParams(window.location.search);
  return qs.get("enterpriseId") || localStorage.getItem("active_enterprise_id") || "";
}

/* =========================
   FETCH HELPERS
========================= */

async function fetchJson(url) {
  const token = localStorage.getItem("token") || "";
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
  return data;
}

function normalizeCases(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.cases) return payload.cases;
  if (payload?.data) return payload.data;
  return [];
}

/**
 * âœ… On charge les cas via l'endpoint qui existe dans ton backend :
 * /api/cases/by-enterprise/{enterpriseId}
 *
 * Si enterpriseId absent, on NE fait pas un call qui 404.
 * On renvoie une liste vide + message clair cà´té UI.
 */
async function fetchCasesList() {
  const enterpriseId = getActiveEnterpriseId();

  if (!enterpriseId) {
    // On ne throw pas pour éviter un écran bloqué : on laisse la page afficher un message
    return { list: [], error: "Enterprise ID introuvable. Ajoute ?enterpriseId=... dans lâ€™URL (ou définis active_enterprise_id)." };
  }

  const data = await fetchJson(`${API_URL}/api/cases/by-enterprise/${enterpriseId}`);
  const list = normalizeCases(data);
  return { list: Array.isArray(list) ? list : [], error: "" };
}

/** âœ… Endpoint 8 radars (existe dans ton backend) */
async function postAllRadarsFromCase(caseId, includeAI) {
  const token = localStorage.getItem("token") || "";

  const res = await fetch(`${API_URL}/api/ai/curperformance-all-radars-from-case`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      case_id: caseId,
      scale: 20,
      include_ai_commentary: includeAI,
    }),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
  return data;
}

/* =========================
   OUTILS AFFICHAGE
========================= */

function safeJsonParse(str) {
  if (!str || typeof str !== "string") return null;
  try {
    return JSON.parse(str.replace(/```json|```/g, "").trim());
  } catch {
    return null;
  }
}

function formatCaseLabel(c) {
  const id = c?._id || c?.id || "";
  const name = c?.company_name || c?.companyName || "Entreprise";
  const date = c?.date_created || c?.timestamp || "";
  const shortId = id ? String(id).slice(0, 6) + "â€¦" : "";
  return `${name}${date ? ` â€” ${date}` : ""}${shortId ? ` (${shortId})` : ""}`;
}

const ORDERED_KEYS = [
  "Environment",
  "Social",
  "Modernization",
  "Lead Time",
  "Cost",
  "Quality",
  "Client / Consumer",
];

/* =========================
   COMPONENT
========================= */

export default function CompareCases() {
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState("");

  const [activeCaseId, setActiveCaseId] = useState(getActiveCaseId());
  const [includeAI, setIncludeAI] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [pdf, setPdf] = useState(null);

  const [globalRadarB64, setGlobalRadarB64] = useState("");
  const [criterionRadars, setCriterionRadars] = useState({});
  const [missingBreakdowns, setMissingBreakdowns] = useState([]);

  const [commentary, setCommentary] = useState(null);
  const commentJson = useMemo(() => safeJsonParse(commentary), [commentary]);

  /* âœ… synchro multi-pages */
  useEffect(() => {
    const onStorage = () => {
      const newId = getActiveCaseId();
      if (newId && newId !== activeCaseId) setActiveCaseId(newId);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [activeCaseId]);

  /* âœ… charger liste des cas */
  useEffect(() => {
    (async () => {
      setCasesLoading(true);
      setCasesError("");

      try {
        const { list, error: listError } = await fetchCasesList();
        setCases(list);

        if (listError) setCasesError(listError);

        // âœ… si pas encore de cas actif, prendre le 1er
        if (!activeCaseId && list.length) {
          const firstId = String(list[0]._id || list[0].id || "");
          setActiveCaseId(firstId);
          localStorage.setItem("active_case_id", firstId);
        }
      } catch (e) {
        setCasesError(e?.message || "Erreur chargement cas");
      } finally {
        setCasesLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  async function loadRadars(caseId, withAI) {
    setError("");
    setLoading(true);

    setCompanyName("");
    setPdf(null);
    setGlobalRadarB64("");
    setCriterionRadars({});
    setMissingBreakdowns([]);
    setCommentary(null);

    try {
      if (!caseId) throw new Error("Aucun cas sélectionné.");

      // âœ… mémoriser enterpriseId si présent (important pour éviter erreurs sur dâ€™autres pages)
      const enterpriseId = getActiveEnterpriseId();
      if (enterpriseId) localStorage.setItem("active_enterprise_id", enterpriseId);

      const data = await postAllRadarsFromCase(caseId, withAI);

      setCompanyName(data.company_name || "");
      setPdf(data.curperformance_pdf || null);

      setGlobalRadarB64(data?.global?.radar_png_base64 || "");
      setCriterionRadars(data?.by_criterion || {});
      setMissingBreakdowns(data?.missing_breakdowns || []);

      setCommentary(data.commentary || null);

      localStorage.setItem("active_case_id", caseId);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeCaseId) loadRadars(activeCaseId, includeAI);
    // eslint-disable-next-line
  }, [activeCaseId, includeAI]);

  return (
    <div className="compareCasesPage">
      <div className="compareCases">
        <header className="compareCases__header">
          <div>
            <h2 className="compareCases__title">CurPerformance â€” Radars</h2>
            <p className="compareCases__subtitle">
              1 radar global + 7 radars détaillés (si sous-scores disponibles).
            </p>
          </div>

          <span className={`pill ${includeAI ? "pill--on" : "pill--off"}`}>
            IA {includeAI ? "ON" : "OFF"}
          </span>
        </header>

        {/* Sélection */}
        <div className="ccCard ccActiveCard">
          <div className="ccField">
            <div className="ccFieldHead">
              <label className="ccLabel">Cas actif</label>
              <span className="ccHintBadge">
                {casesLoading ? "Loadingâ€¦" : `${cases.length} cas`}
              </span>
            </div>

            <select
              className="ccSelect"
              value={activeCaseId}
              onChange={(e) => setActiveCaseId(e.target.value)}
              disabled={casesLoading || !cases.length}
            >
              {!cases.length && <option value="">Aucun cas</option>}
              {cases.map((c) => {
                const id = String(c._id || c.id || "");
                return (
                  <option key={id} value={id}>
                    {formatCaseLabel(c)}
                  </option>
                );
              })}
            </select>

            {casesError && <div className="ccMiniError">{casesError}</div>}
          </div>

          <label className="ccToggleRow">
            <input
              type="checkbox"
              checked={includeAI}
              onChange={(e) => setIncludeAI(e.target.checked)}
              disabled={!activeCaseId}
            />
            Commentaire IA
          </label>

          <div className="ccActions">
            <button
              className="ccBtn ccBtn--primary"
              onClick={() => loadRadars(activeCaseId, includeAI)}
              disabled={loading || !activeCaseId}
            >
              {loading ? "Génération..." : "Régénérer"}
            </button>

            {pdf?.file_id && (
              <a
                className="ccBtn ccBtn--ghost"
                href={`${API_URL}/api/pdf/${encodeURIComponent(pdf.file_id)}`}
                target="_blank"
                rel="noreferrer"
              >
                Ouvrir PDF CurPerformance
              </a>
            )}
          </div>
        </div>

        {error && <div className="ccError">{error}</div>}

        {/* Affichage */}
        <div className="ccCard radarCard">
          <div className="radarCard__top">
            <h3 className="radarCard__title">{companyName || "Entreprise"}</h3>
          </div>

          <div className="radarGrid">
            {/* Global */}
            <div className="radarTile radarTile--global">
              <div className="radarTile__head">
                <div className="radarTile__title">Global</div>
                <div className="radarTile__sub">7 critères</div>
              </div>

              {globalRadarB64 ? (
                <div className="radarTile__imgWrap">
                  <img
                    className="radarTile__img"
                    src={`data:image/png;base64,${globalRadarB64}`}
                    alt="Radar Global"
                  />
                </div>
              ) : (
                <div className="radarTile__placeholder">
                  {loading ? "Génération..." : "Aucun radar"}
                </div>
              )}
            </div>

            {/* Détails */}
            {ORDERED_KEYS.map((k) => {
              const b64 = criterionRadars?.[k];
              const isMissing =
                Array.isArray(missingBreakdowns) && missingBreakdowns.includes(k);

              return (
                <div className="radarTile" key={k}>
                  <div className="radarTile__head">
                    <div className="radarTile__title">{k}</div>
                    {isMissing ? (
                      <span className="radarTile__badge radarTile__badge--warn">
                        Sous-scores manquants
                      </span>
                    ) : (
                      <span className="radarTile__badge">Détail</span>
                    )}
                  </div>

                  {b64 ? (
                    <div className="radarTile__imgWrap">
                      <img
                        className="radarTile__img"
                        src={`data:image/png;base64,${b64}`}
                        alt={`Radar ${k}`}
                      />
                    </div>
                  ) : (
                    <div className="radarTile__placeholder">
                      {loading ? "Génération..." : "Pas de radar détaillé"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Commentaire */}
          <div className="radarCard__comment">
            <div className="radarCard__commentHeader">
              <h4 className="radarCard__sectionTitle">Commentaire</h4>
              {!includeAI && <span className="radarCard__badge">IA désactivée</span>}
            </div>

            {!includeAI && (
              <p className="radarCard__hint">
                Active â€œCommentaire IAâ€ pour générer lâ€™analyse.
              </p>
            )}

            {includeAI && !commentary && (
              <p className="radarCard__hint">Commentaire non fourni.</p>
            )}

            {commentJson ? (
              <pre className="radarCard__pre">{JSON.stringify(commentJson, null, 2)}</pre>
            ) : (
              commentary && <pre className="radarCard__pre">{commentary}</pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
